import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  type CardColumnConfig,
  DEFAULT_CARD_COLUMNS,
  DEFAULT_TASK_COLUMNS,
  type SquadId,
  type TaskColumnConfig,
  type TaskWithCard,
} from '@voll-intelligence/domain';

import { queryKeys } from '@web/core/query';
import { boardRepository, meRepository } from '@web/data/repositories';
import type { ConversationBoardResponse, TaskBoardResponse } from '@web/data/repositories';
import { useOrgContext } from '@web/ui/core';

/**
 * URL-stable slugs for the board mode. Code identifiers in English; pt-BR UI
 * labels live in the i18n bundle. Mode only drives rendering — both boards are
 * fetched up front so switching is instant.
 */
export type BoardMode = 'conversations' | 'tasks';

export interface BoardState {
  orgId: string;
  orgSlug: string;
  squadId: SquadId;
  conversationQuery: UseQueryResult<ConversationBoardResponse>;
  /** Grouped task board — excludes the viewing user's active tasks (server-side). */
  taskQuery: UseQueryResult<TaskBoardResponse>;
  /** The viewing user's active (non-done) tasks — the "Minhas tarefas" hero. */
  myTasks: readonly TaskWithCard[];
  /** Display name for the hero "you" pill. */
  userName: string;
  cardColumns: readonly CardColumnConfig[];
  taskColumns: readonly TaskColumnConfig[];
}

/**
 * Squad-scoped board orchestrator. Owns every board query so the page stays
 * thin: the conversation board, the grouped task board (excluding the user's
 * active tasks), and the flat "my active tasks" list for the hero. All
 * filtering happens server-side. Both boards fetch regardless of the rendered
 * mode.
 */
export function useBoard(squadId: SquadId): BoardState {
  const { orgId, orgSlug } = useOrgContext();

  // Display name for the hero "you" pill. The viewing user's agent (and thus
  // which tasks are "mine" / excluded from the columns) is resolved
  // server-side from the auth token — never passed from the client.
  const meQuery = useQuery({ queryKey: queryKeys.me.all, queryFn: () => meRepository.getMe() });
  const userName = meQuery.data?.displayName ?? meQuery.data?.name ?? '';

  const conversationQuery = useQuery({
    queryKey: queryKeys.boards.squadConversation(orgId, squadId),
    queryFn: () => boardRepository.loadSquadConversationBoard(orgId, squadId),
  });

  const taskQuery = useQuery({
    queryKey: queryKeys.boards.squadTasks(orgId, squadId),
    queryFn: () => boardRepository.loadSquadTaskBoard(orgId, squadId),
  });

  const myTasksQuery = useQuery({
    queryKey: queryKeys.boards.squadMyTasks(orgId, squadId),
    queryFn: () => boardRepository.loadMyTasks(orgId, squadId),
  });

  const boardConfigQuery = useQuery({
    queryKey: queryKeys.orgBoardConfig(orgId),
    queryFn: () => boardRepository.loadBoardConfig(orgId),
  });

  const cardColumns = boardConfigQuery.data?.cardColumns ?? DEFAULT_CARD_COLUMNS;
  const taskColumns = boardConfigQuery.data?.taskColumns ?? DEFAULT_TASK_COLUMNS;

  return {
    orgId,
    orgSlug,
    squadId,
    conversationQuery,
    taskQuery,
    myTasks: myTasksQuery.data ?? [],
    userName,
    cardColumns,
    taskColumns,
  };
}
